<template>
  <v-container fluid class="pa-3">
    <v-card outlined class="stats-card">
      <v-card-title class="pb-1">
        <div>
          <div class="text-h6">Attorney Stats</div>
          <div
            v-if="selectedPeriod"
            class="text-caption grey--text text--darken-1"
          >
            {{ selectedPeriod.label }} · {{ formatRange(selectedPeriod) }}
          </div>
        </div>
      </v-card-title>

      <v-card-text class="pt-2">
        <div class="text-caption grey--text text--darken-1 mb-2">
          Last 90 days is the default.
          <span v-if="viewerIsDivisionAttorney">
            Choose a quarter to compare your rank.
          </span>
          <span v-else>Choose a quarter to see that ranking.</span>
          Ranks follow {{ sortLabel }} ({{ sortDirectionLabel }}).
        </div>

        <div class="period-row">
          <v-btn
            v-for="period in periodOptions"
            :key="period.id"
            small
            depressed
            class="mr-2 mb-2"
            :color="period.id === selectedPeriodId ? 'primary' : undefined"
            :outlined="period.id !== selectedPeriodId"
            @click="selectedPeriodId = period.id"
          >
            {{ period.label }}
            <span v-if="period.viewerRank" class="ml-1 font-weight-bold">
              #{{ period.viewerRank }}
            </span>
          </v-btn>
        </div>

        <v-alert
          v-if="viewerIsDivisionAttorney && selfSummary"
          text
          dense
          color="primary"
          class="mt-2 mb-3"
        >
          You are ranked <strong>#{{ selfSummary.rank }}</strong> of
          {{ selfSummary.total }} for {{ sortLabel }} ({{ sortDirectionLabel }})
          <span v-if="selectedPeriod">in {{ selectedPeriod.label }}</span
          >.
          <span v-if="previousQuarterComparison">
            {{ previousQuarterComparison }}
          </span>
        </v-alert>
        <v-alert
          v-else-if="viewerIsDivisionAttorney && !$apollo.loading"
          text
          dense
          color="grey"
          class="mt-2 mb-3"
        >
          You have no settled cases in {{ selectedPeriod.label }}, so you are
          not ranked for this period.
        </v-alert>

        <div class="text-caption grey--text text--darken-1 mb-2">
          <span v-if="showAll">Showing every division attorney.</span>
          <span v-else-if="viewerIsDivisionAttorney">
            The top 10 names and stats are shown. Lower ranks stay on the list
            with the name and stats hidden, and your row stays visible if you
            rank below 10.
          </span>
          <span v-else>
            The top 10 names and stats are shown. Lower ranks stay on the list
            with the name and stats hidden.
          </span>
        </div>

        <v-progress-linear
          v-if="$apollo.loading"
          indeterminate
          color="primary"
          class="mb-2"
        ></v-progress-linear>
        <v-alert v-if="$apollo.error" type="error" text dense>
          Attorney stats could not be loaded.
        </v-alert>

        <v-data-table
          ref="statsTable"
          :headers="headers"
          :items="tableItems"
          item-key="id"
          :item-class="rowClass"
          class="elevation-0 attorney-stats-table"
          fixed-header
          height="70vh"
          :items-per-page="-1"
          hide-default-footer
          :no-data-text="emptyText"
          @click.native="onTableClick"
        >
          <template v-for="header in headers" v-slot:[`header.${header.value}`]>
            <span
              :key="header.value"
              :class="header.value === 'rank' ? '' : 'stats-sort-header'"
            >
              {{ header.text }}
              <v-icon v-if="header.value !== 'rank'" small class="ml-1">
                {{ sortIcon(header.value) }}
              </v-icon>
            </span>
          </template>

          <template v-slot:item.rank="{ item }">
            {{ item.rank }}
          </template>
          <template v-slot:item.name="{ item }">
            <span>{{ item.muted ? "Hidden" : item.name }}</span>
            <span v-if="item.isSelf" class="you-label ml-2">You</span>
          </template>
          <template v-slot:item.totalCases="{ item }">
            {{ statCell(item, item.totalCases) }}
          </template>
          <template v-slot:item.averageFee="{ item }">
            {{ statCell(item, formatMoney(item.averageFee)) }}
          </template>
          <template v-slot:item.qualifyingCases="{ item }">
            {{ statCell(item, item.qualifyingCases) }}
          </template>
          <template v-slot:item.normalRangeAverage="{ item }">
            {{ statCell(item, formatMoney(item.normalRangeAverage)) }}
          </template>
          <template v-slot:item.normalRangeCases="{ item }">
            {{ statCell(item, item.normalRangeCases) }}
          </template>
          <template v-slot:item.policyLimitsExhaustedCount="{ item }">
            {{ statCell(item, item.policyLimitsExhaustedCount) }}
          </template>
          <template v-slot:item.policyLimitsPercent="{ item }">
            {{ statCell(item, formatPercent(item.policyLimitsPercent)) }}
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import gql from "graphql-tag";
import { mapGetters } from "vuex";
import accounting from "accounting";
import {
  format,
  subDays,
  subQuarters,
  startOfQuarter,
  endOfQuarter,
  getQuarter,
  getYear
} from "date-fns";
import { hasRole } from "../util";

const TOP_VISIBLE = 10;
const QUARTER_COUNT = 8;

export default {
  data() {
    return {
      settledCases: [],
      organization: null,
      selectedPeriodId: "last90",
      sortBy: ["policyLimitsPercent"],
      sortDesc: [true]
    };
  },
  apollo: {
    settledCases: {
      query: gql`
        query($startDate: String!, $endDate: String!) {
          fullySettledCasesInPeriod(startDate: $startDate, endDate: $endDate) {
            caseId
            attorneyName
            attorneyStaffCode
            totalFees
            policyLimitsExhausted
            dateSettled
          }
        }
      `,
      variables() {
        return {
          startDate: this.queryStart,
          endDate: this.queryEnd
        };
      },
      fetchPolicy: "no-cache",
      update(data) {
        return data.fullySettledCasesInPeriod || [];
      }
    },
    organization: {
      query: gql`
        query($id: ID!) {
          organization(id: $id) {
            id
            divisions {
              id
              active
              teams {
                id
                active
                members {
                  role
                  user {
                    id
                    firstName
                    lastName
                    staffCode
                  }
                }
              }
            }
          }
        }
      `,
      variables() {
        return { id: String(this.organizationId) };
      },
      skip() {
        return !this.organizationId;
      },
      fetchPolicy: "no-cache",
      update(data) {
        return data.organization;
      }
    }
  },
  computed: {
    ...mapGetters(["currentUser"]),
    showAll() {
      if (!this.currentUser) return false;
      return (
        hasRole(this.currentUser, "admin") ||
        hasRole(this.currentUser, "overlord")
      );
    },
    organizationId() {
      const organization = this.currentUser && this.currentUser.organization;
      return organization && organization.id;
    },
    divisionAttorneyIndex() {
      const codes = new Set();
      const names = new Set();
      const ids = new Set();
      const divisions =
        (this.organization && this.organization.divisions) || [];
      divisions.forEach(division => {
        if (division.active === false) return;
        (division.teams || []).forEach(team => {
          if (team.active === false) return;
          (team.members || []).forEach(member => {
            if (!member || member.role !== "attorney" || !member.user) return;
            const user = member.user;
            if (user.id != null) ids.add(String(user.id));
            this.codeTokens(user.staffCode).forEach(code => codes.add(code));
            this.nameKeys(user).forEach(key => names.add(key));
          });
        });
      });
      return { codes, names, ids };
    },
    viewerIsDivisionAttorney() {
      const user = this.currentUser;
      const index = this.divisionAttorneyIndex;
      if (!user || !this.organization) return false;
      if (index.ids.has(String(user.id))) return true;
      if (this.codeTokens(user.staffCode).some(code => index.codes.has(code))) {
        return true;
      }
      return this.nameKeys(user).some(key => index.names.has(key));
    },
    viewerCodes() {
      const codes = new Set(
        this.codeTokens(this.currentUser && this.currentUser.staffCode)
      );
      const userId = this.currentUser && String(this.currentUser.id);
      const divisions =
        (this.organization && this.organization.divisions) || [];
      divisions.forEach(division => {
        if (division.active === false) return;
        (division.teams || []).forEach(team => {
          if (team.active === false) return;
          (team.members || []).forEach(member => {
            if (!member || member.role !== "attorney" || !member.user) return;
            if (String(member.user.id) !== userId) return;
            this.codeTokens(member.user.staffCode).forEach(code =>
              codes.add(code)
            );
          });
        });
      });
      return [...codes];
    },
    emptyText() {
      if (this.$apollo.loading) return "Loading attorney stats...";
      return "No division attorneys settled a case in this period";
    },
    sortKey() {
      return this.sortBy[0] || "policyLimitsPercent";
    },
    sortDescending() {
      return this.sortDesc[0] !== false;
    },
    sortLabel() {
      const header = this.headers.find(item => item.value === this.sortKey);
      return header ? header.text : "Policy Limits %";
    },
    sortDirectionLabel() {
      if (this.sortKey === "name") {
        return this.sortDescending ? "Z to A" : "A to Z";
      }
      return this.sortDescending ? "highest first" : "lowest first";
    },
    headers() {
      return [
        { text: "Rank", value: "rank", sortable: false },
        { text: "Attorney", value: "name", sortable: false },
        { text: "Total Cases", value: "totalCases", sortable: false },
        { text: "Average Fee (>$2K)", value: "averageFee", sortable: false },
        {
          text: "Qualifying Cases",
          value: "qualifyingCases",
          sortable: false
        },
        {
          text: "Normal Range Average ($2K-$1M)",
          value: "normalRangeAverage",
          sortable: false
        },
        {
          text: "Normal Range Cases",
          value: "normalRangeCases",
          sortable: false
        },
        {
          text: "Policy Limits Exhausted",
          value: "policyLimitsExhaustedCount",
          sortable: false
        },
        {
          text: "Policy Limits %",
          value: "policyLimitsPercent",
          sortable: false
        }
      ];
    },
    periods() {
      const today = new Date();
      const last90 = {
        id: "last90",
        label: "Last 90 days",
        start: format(subDays(today, 90), "yyyy-MM-dd"),
        end: format(today, "yyyy-MM-dd")
      };
      const quarters = [];
      for (let index = 0; index < QUARTER_COUNT; index += 1) {
        const anchor = subQuarters(today, index);
        const start = startOfQuarter(anchor);
        const end = index === 0 ? today : endOfQuarter(anchor);
        quarters.push({
          id: `q-${getYear(start)}-${getQuarter(start)}`,
          label: `Q${getQuarter(start)} ${getYear(start)}`,
          start: format(start, "yyyy-MM-dd"),
          end: format(end, "yyyy-MM-dd")
        });
      }
      return [last90, ...quarters];
    },
    selectedPeriod() {
      return (
        this.periods.find(period => period.id === this.selectedPeriodId) ||
        this.periods[0]
      );
    },
    queryStart() {
      return this.periods.map(period => period.start).sort()[0];
    },
    queryEnd() {
      return this.periods
        .map(period => period.end)
        .sort()
        .slice(-1)[0];
    },
    periodOptions() {
      return this.periods.map(period => {
        const ranked = this.rankRows(this.casesInRange(period));
        const viewer = this.viewerIsDivisionAttorney
          ? ranked.find(row => row.isSelf)
          : null;
        return {
          ...period,
          viewerRank: viewer ? viewer.rank : null
        };
      });
    },
    tableItems() {
      return this.rankRows(this.casesInRange(this.selectedPeriod));
    },
    selfSummary() {
      const viewer = this.tableItems.find(row => row.isSelf);
      if (!viewer) return null;
      return {
        rank: viewer.rank,
        total: this.tableItems.length
      };
    },
    previousQuarterComparison() {
      if (!this.selectedPeriod || this.selectedPeriod.id === "last90") {
        return "";
      }
      const quarters = this.periodOptions.filter(period =>
        period.id.startsWith("q-")
      );
      const index = quarters.findIndex(
        period => period.id === this.selectedPeriodId
      );
      const previous = quarters[index + 1];
      if (!previous || !this.selfSummary) return "";
      if (!previous.viewerRank) {
        return `You were not ranked in ${previous.label}.`;
      }
      return `In ${previous.label} you were #${previous.viewerRank}.`;
    },
    tableSignature() {
      const viewer = this.tableItems.find(row => row.isSelf);
      return [
        this.selectedPeriodId,
        this.sortKey,
        this.sortDescending,
        viewer ? viewer.rank : "none",
        this.tableItems.length
      ].join("|");
    }
  },
  watch: {
    tableSignature() {
      this.$nextTick(() => this.scrollToViewer());
    }
  },
  methods: {
    casesInRange(period) {
      if (!period || !Array.isArray(this.settledCases)) return [];
      return this.settledCases.filter(case_ => {
        const date = String(case_.dateSettled || "").slice(0, 10);
        return date >= period.start && date <= period.end;
      });
    },
    rankRows(cases) {
      return this.buildAnalytics(cases)
        .slice()
        .sort((a, b) => this.compareBy(a, b, this.sortKey, this.sortDescending))
        .map((row, index) => {
          const rank = index + 1;
          return {
            ...row,
            rank,
            muted: !this.showAll && rank > TOP_VISIBLE && !row.isSelf
          };
        });
    },
    buildAnalytics(cases) {
      const grouped = {};
      cases.forEach(case_ => {
        const staffCode = this.normalizeCode(case_.attorneyStaffCode);
        const name =
          String(case_.attorneyName || "").trim() || staffCode || "Unknown";
        const key = staffCode || `name:${name.toLowerCase()}`;
        if (!grouped[key]) {
          grouped[key] = {
            name,
            staffCode,
            cases: []
          };
        }
        if (
          name !== staffCode &&
          grouped[key].name === grouped[key].staffCode
        ) {
          grouped[key].name = name;
        }
        grouped[key].cases.push(case_);
      });

      return Object.keys(grouped)
        .map(key => {
          const group = grouped[key];
          const caseList = group.cases;
          const qualifyingCases = caseList.filter(
            case_ => this.caseFee(case_) > 2000
          );
          const normalRangeCases = caseList.filter(case_ => {
            const fees = this.caseFee(case_);
            return fees > 2000 && fees <= 1000000;
          });
          const policyLimitsExhaustedCount = caseList.filter(case_ =>
            this.isPolicyLimitsExhausted(case_)
          ).length;
          const row = {
            id: key,
            name: group.name,
            staffCode: group.staffCode,
            totalCases: caseList.length,
            qualifyingCases: qualifyingCases.length,
            normalRangeCases: normalRangeCases.length,
            averageFee: this.averageFee(qualifyingCases),
            normalRangeAverage: this.averageFee(normalRangeCases),
            policyLimitsExhaustedCount,
            policyLimitsPercent: caseList.length
              ? (policyLimitsExhaustedCount / caseList.length) * 100
              : 0
          };
          row.isSelf = this.isViewer(row);
          return row;
        })
        .filter(row => this.rowInDivision(row));
    },
    compareBy(a, b, sortKey, sortDescending, useNameTieBreak = true) {
      const byName = () =>
        String(a.name).localeCompare(String(b.name), undefined, {
          sensitivity: "base"
        });
      const byId = () => String(a.id).localeCompare(String(b.id));
      if (sortKey === "name") {
        const cmp = byName();
        return sortDescending ? -cmp : cmp;
      }
      const cmp = (Number(a[sortKey]) || 0) - (Number(b[sortKey]) || 0);
      if (cmp === 0) return useNameTieBreak ? byName() : byId();
      return sortDescending ? -cmp : cmp;
    },
    isViewer(row) {
      if (!this.viewerIsDivisionAttorney || !this.currentUser || !row) {
        return false;
      }
      if (row.staffCode && this.viewerCodes.includes(row.staffCode)) {
        return true;
      }
      return this.viewerNameMatches(row.name);
    },
    codeTokens(value) {
      return String(value || "")
        .toUpperCase()
        .split(/[^A-Z0-9]+/)
        .filter(Boolean);
    },
    nameKeys(user) {
      if (!user) return [];
      const first = this.compactName(user.firstName);
      const last = this.compactName(user.lastName);
      if (!first || !last) return [];
      return [`${last}${first}`, `${first}${last}`];
    },
    rowInDivision(row) {
      const index = this.divisionAttorneyIndex;
      if (!index || !row) return false;
      if (row.staffCode && index.codes.has(row.staffCode)) return true;
      const compact = this.compactName(row.name);
      if (!compact) return false;
      return [...index.names].some(
        key => compact === key || compact.startsWith(key)
      );
    },
    onTableClick(event) {
      const headerCell = event.target.closest("thead th");
      if (!headerCell || !headerCell.parentNode) return;
      const index = Array.prototype.indexOf.call(
        headerCell.parentNode.children,
        headerCell
      );
      const column = this.headers[index];
      if (!column) return;
      this.toggleSort(column.value);
    },
    viewerNameMatches(attorneyName) {
      const user = this.currentUser;
      if (!user) return false;
      const compact = this.compactName(attorneyName);
      const codeMatch = this.viewerCodes.some(
        code => compact === code.toLowerCase()
      );
      if (codeMatch) return true;
      const first = this.compactName(user.firstName);
      const last = this.compactName(user.lastName);
      if (!compact || !first || !last) return false;
      const lastFirst = `${last}${first}`;
      const firstLast = `${first}${last}`;
      return (
        compact === lastFirst ||
        compact === firstLast ||
        compact.startsWith(lastFirst) ||
        compact.startsWith(firstLast)
      );
    },
    toggleSort(value) {
      if (value === "rank") return;
      if (this.sortKey === value) {
        this.sortDesc = [!this.sortDescending];
        return;
      }
      this.sortBy = [value];
      this.sortDesc = [value !== "name"];
    },
    sortIcon(value) {
      if (this.sortKey !== value) return "mdi-unfold-more-horizontal";
      return this.sortDescending ? "mdi-arrow-down" : "mdi-arrow-up";
    },
    rowClass(item) {
      if (item.isSelf) return "attorney-stats-self";
      if (item.muted) return "attorney-stats-muted";
      return "";
    },
    scrollToViewer() {
      if (
        this.showAll ||
        !this.selfSummary ||
        this.selfSummary.rank <= TOP_VISIBLE
      ) {
        return;
      }
      const table = this.$refs.statsTable;
      if (!table || !table.$el) return;
      const wrapper = table.$el.querySelector(".v-data-table__wrapper");
      const row = table.$el.querySelector("tr.attorney-stats-self");
      if (!wrapper || !row) return;
      const rowRect = row.getBoundingClientRect();
      const wrapRect = wrapper.getBoundingClientRect();
      wrapper.scrollTop +=
        rowRect.top -
        wrapRect.top -
        wrapper.clientHeight / 2 +
        rowRect.height / 2;
    },
    caseFee(case_) {
      return accounting.unformat(case_ && case_.totalFees) || 0;
    },
    averageFee(cases) {
      if (!cases.length) return 0;
      return (
        cases.reduce((sum, case_) => sum + this.caseFee(case_), 0) /
        cases.length
      );
    },
    isPolicyLimitsExhausted(case_) {
      if (!case_) return false;
      return (
        case_.policyLimitsExhausted === true ||
        case_.policyLimitsExhausted === 1 ||
        case_.policyLimitsExhausted === "1"
      );
    },
    normalizeCode(value) {
      return String(value || "")
        .trim()
        .toUpperCase();
    },
    compactName(value) {
      return String(value || "")
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "");
    },
    statCell(item, value) {
      if (item && item.muted) return "—";
      return value;
    },
    formatMoney(value) {
      return accounting.formatMoney(value || 0);
    },
    formatPercent(value) {
      return `${(Number(value) || 0).toFixed(1)}%`;
    },
    formatRange(period) {
      if (!period) return "";
      return `${this.formatPretty(period.start)} – ${this.formatPretty(
        period.end
      )}`;
    },
    formatPretty(isoDate) {
      const [year, month, day] = String(isoDate)
        .split("-")
        .map(Number);
      if (!year || !month || !day) return isoDate;
      return format(new Date(year, month - 1, day), "MMM d, yyyy");
    }
  }
};
</script>

<style scoped>
.stats-card {
  border: 1px solid #e0e0e0;
}

.period-row {
  display: flex;
  flex-wrap: wrap;
}

.stats-sort-header {
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
}

.you-label {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #1565c0;
}

.attorney-stats-table >>> tbody tr.attorney-stats-muted {
  background: #f3f3f3;
}

.attorney-stats-table >>> tbody tr.attorney-stats-muted td {
  color: #616161 !important;
}

.attorney-stats-table >>> tbody tr.attorney-stats-self {
  background: #e3f2fd !important;
}

.attorney-stats-table >>> tbody tr.attorney-stats-self td {
  color: rgba(0, 0, 0, 0.87) !important;
  font-weight: 600;
}
</style>
